/**
 * Reports page — generate, list, and download PDF security reports.
 */

import { useState } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useFindingSummary } from '@/hooks/useFindings';
import { getReports, createReport, downloadReport, deleteReport, exportFindingsCsv } from '@/api/reports';
import { getScans } from '@/api/scans';
import ReportList from '@/components/reports/ReportList';
import ReportDownload from '@/components/reports/ReportDownload';
import ExecutiveSummary from '@/components/reports/ExecutiveSummary';
import Modal from '@/components/common/Modal';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import type { Report, ReportCreateRequest } from '@/types/report';
import { apiError } from '@/utils/apiError';

export default function ReportsPage() {
  const { isCustomerAdmin, user } = useAuth();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  const { data: reports, isLoading,error:reportsError } = useQuery({
    queryKey: ['reports'],
    queryFn: getReports,
    // Poll every 3 s while any report is still in progress; stop once all settle.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 10_000;
      const hasActive = data.some(
        (r: Report) => r.status === 'pending' || r.status === 'generating',
      );
      return hasActive ? 3_000 : false;
    },
  });

  const sourceScans = useInfiniteQuery({
    queryKey: ['report-scans'], initialPageParam:0,
    queryFn: ({pageParam}) => getScans('completed',{skip:pageParam,limit:100}),
    getNextPageParam:(lastPage,pages)=>lastPage.length===100?pages.length*100:undefined,
  });
  const scans=sourceScans.data?.pages.flat()??[];

  const { data: findingSummary } = useFindingSummary();

  const createMutation = useMutation({
    mutationFn: (payload: ReportCreateRequest) => createReport(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (reportId: string) => deleteReport(reportId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reports'] }),
  });

  async function handleDownload(report: Report) {
    try {
      const blob = await downloadReport(report.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.name}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download report.');
    }
  }

  async function handleExportCsv() {
    try {
      await exportFindingsCsv();
    } catch {
      alert('Failed to export CSV.');
    }
  }

  if (isLoading||sourceScans.isLoading) return <LoadingSpinner label="Loading reports..." />;

  return (
    <div className="space-y-6">
      {(reportsError||sourceScans.error||deleteMutation.error)&&<p role="alert" className="rounded bg-red-50 p-3 text-red-700">{apiError(reportsError||sourceScans.error||deleteMutation.error)}</p>}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <div className="flex gap-2">
          <button onClick={handleExportCsv} className="btn-secondary">
            Export CSV
          </button>
          <button onClick={() => setShowSummary(true)} className="btn-secondary">
            Executive Summary
          </button>
          {isCustomerAdmin && (
            <button onClick={() => setShowForm(true)} className="btn-primary">
              Generate Report
            </button>
          )}
        </div>
      </div>

      <ReportList
        canManage={isCustomerAdmin}
        reports={reports ?? []}
        onDownload={handleDownload}
        onDelete={(report) => {
          if (window.confirm(`Delete report "${report.name}"?`)) {
            deleteMutation.mutate(report.id);
          }
        }}
      />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="Generate Report" size="lg">
        {createMutation.error&&<p role="alert" className="mb-3 text-red-700">{apiError(createMutation.error)}</p>}
        <ReportDownload
          scans={scans}
          hasMoreScans={sourceScans.hasNextPage}
          onLoadMoreScans={()=>sourceScans.fetchNextPage()}
          isLoadingMoreScans={sourceScans.isFetchingNextPage}
          onSubmit={(payload) => createMutation.mutate(payload)}
          onCancel={() => setShowForm(false)}
          isSubmitting={createMutation.isPending}
        />
      </Modal>

      <Modal isOpen={showSummary} onClose={() => setShowSummary(false)} title="Executive Summary" size="xl">
        {findingSummary ? (
          <ExecutiveSummary
            summary={findingSummary}
            tenantName={user?.email?.split('@')[1] ?? 'Your Organization'}
          />
        ) : (
          <LoadingSpinner label="Loading summary..." />
        )}
      </Modal>
    </div>
  );
}
